import React, { useState, useMemo, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import LaunchIcon from '@material-ui/icons/Launch';
import { greens, golds, reds, greys, blues, onwardNavy } from '@/styles/colors';
import { ModalContext } from '@/components/ShipmentForm/modals/EditOrderModal';
import OrderDetailsPricing from '@/components/ShipmentForm/modals/EditOrderModal/OrderDetailsTab/OrderDetailsPricing';
import DetailPanel from './DetailPanel';
import {
    auditPerspectives,
    carrierPerspectiveName,
    orderShipperTotal,
    orderCarrierTotal,
    orderPerspectiveTotal,
    orderMarginDollar,
    orderMarginPct,
    routeMarginDollar,
    routeMarginPct,
    marginHealth,
    canViewShipperRates,
    canViewShipperAggregate,
} from './utils';

const HEALTH_COLORS = { good: greens[0], thin: golds[0], bad: reds.cancelled, neutral: greys[1] };
const HEALTH_BG = { good: '#eaf6ec', thin: '#fbf1e0', bad: '#fbeaec', neutral: '#f0f0f0' };

const fmt$ = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function mergeChanges(base, incoming) {
    if (!base || !Object.keys(base).length) return incoming || {};
    if (!incoming || !Object.keys(incoming).length) return base;
    const merged = { ...base, ...incoming };
    if (base.order_revenue_adjustments || incoming.order_revenue_adjustments) {
        merged.order_revenue_adjustments = {
            ...(base.order_revenue_adjustments || {}),
            ...(incoming.order_revenue_adjustments || {}),
        };
    }
    return merged;
}

function MarginChip({ pct }) {
    const health = marginHealth(pct);
    return (
        <span
            css={css`
                font-family: Montserrat, Roboto, Arial, sans-serif;
                font-weight: 700;
                font-size: 12px;
                padding: 3px 9px;
                border-radius: 20px;
                color: ${HEALTH_COLORS[health]};
                background: ${HEALTH_BG[health]};
            `}
        >
            {pct != null ? `${pct.toFixed(1)}%` : '–'}
        </span>
    );
}

function MarginBar({ clonedOrder, allClonedOrders, baselineRouteMargin, showRouteMargin, showShipperFigures }) {
    const shipperTotal = orderShipperTotal(clonedOrder);
    const carrierTotal = orderCarrierTotal(clonedOrder);
    const orderMargin = orderMarginDollar(clonedOrder);
    const orderMarginPercent = orderMarginPct(clonedOrder);
    const health = showShipperFigures ? marginHealth(orderMarginPercent) : 'neutral';

    const routeMargin = routeMarginDollar(allClonedOrders);
    const routeMarginPercent = routeMarginPct(allClonedOrders);
    const routeMarginDelta = routeMargin - baselineRouteMargin;
    const routeHealth = HEALTH_COLORS[marginHealth(routeMarginPercent)];

    const block = (label, content, caption) => (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                gap: 3px;
                min-width: 0;
            `}
        >
            <span
                css={css`
                    font-family: Montserrat, Roboto, Arial, sans-serif;
                    font-weight: 700;
                    font-size: 10.5px;
                    color: ${greys[1]};
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                `}
            >
                {label}
            </span>
            {content}
            {caption && (
                <span
                    css={css`
                        font-family: Montserrat, Roboto, Arial, sans-serif;
                        font-weight: 600;
                        font-size: 11.5px;
                        color: ${greys[1]};
                    `}
                >
                    {caption}
                </span>
            )}
        </div>
    );

    const val = (text, color = onwardNavy, size = 19) => (
        <span
            css={css`
                font-family: Montserrat, Roboto, Arial, sans-serif;
                font-weight: 800;
                font-size: ${size}px;
                color: ${color};
                letter-spacing: -0.01em;
            `}
        >
            {text}
        </span>
    );

    return (
        <div
            css={css`
                display: flex;
                align-items: center;
                gap: 18px;
                padding: 14px 20px;
                border: 1px solid ${greys[4]};
                border-left: 4px solid ${HEALTH_COLORS[health]};
                border-radius: 10px;
                background: white;
            `}
        >
            {showShipperFigures && (
                <>
                    {block('Shipper', val(fmt$.format(shipperTotal)))}
                    <span
                        css={css`
                            color: ${greys[4]};
                            font-size: 18px;
                            flex-shrink: 0;
                        `}
                    >
                        −
                    </span>
                </>
            )}
            {block(carrierPerspectiveName(clonedOrder), val(fmt$.format(carrierTotal)))}
            {showShipperFigures && (
                <>
                    <span
                        css={css`
                            color: ${greys[4]};
                            font-size: 18px;
                            flex-shrink: 0;
                        `}
                    >
                        =
                    </span>
                    {block(
                        'Order Margin',
                        <div
                            css={css`
                                display: flex;
                                align-items: baseline;
                                gap: 8px;
                            `}
                        >
                            {val((orderMargin > 0 ? '+' : '') + fmt$.format(orderMargin), HEALTH_COLORS[health], 22)}
                            <MarginChip pct={orderMarginPercent} />
                        </div>
                    )}
                </>
            )}
            {showRouteMargin && (
                <>
                    <div
                        css={css`
                            width: 1px;
                            height: 38px;
                            background: ${greys[4]};
                            margin-left: auto;
                            flex-shrink: 0;
                        `}
                    />
                    {block(
                        'Route Margin',
                        <div
                            css={css`
                                display: flex;
                                align-items: baseline;
                                gap: 8px;
                            `}
                        >
                            {val((routeMargin > 0 ? '+' : '') + fmt$.format(routeMargin), routeHealth, 19)}
                            <span
                                css={css`
                                    font-family: Montserrat, Roboto, Arial, sans-serif;
                                    font-weight: 700;
                                    font-size: 12px;
                                    color: ${routeHealth};
                                `}
                            >
                                {routeMarginPercent != null ? `${routeMarginPercent.toFixed(1)}%` : '–'}
                            </span>
                        </div>,
                        Math.abs(routeMarginDelta) < 0.005
                            ? 'No change this session'
                            : `${routeMarginDelta > 0 ? '▲ +' : '▼ '}${fmt$.format(
                                  Math.abs(routeMarginDelta)
                              )} this session`
                    )}
                </>
            )}
        </div>
    );
}

export default function OrderView({
    order,
    clonedOrder,
    orderIndex,
    totalOrders,
    onModifyOrder,
    onFlushSave,
    flushRef,
    allClonedOrders,
    baselineRouteMargin,
    onLiveOrderChange,
    canEditPerspective,
    showAllBreakdowns = false,
    showRouteMargin = true,
}) {
    const tabs = useMemo(
        () => auditPerspectives({ order, canEditPerspective, showAllBreakdowns }),
        [order, canEditPerspective, showAllBreakdowns]
    );

    // Gated on perspective access alone, not on whether an override row happens to exist — the
    // shipper rate drives the margin readouts whether or not it has its own tab.
    const showShipperFigures = canViewShipperRates(order, canEditPerspective);
    const showRouteFigures = showRouteMargin && canViewShipperAggregate(allClonedOrders, canEditPerspective);

    const [activeTab, setActiveTab] = useState(() => tabs[0]?.perspective);

    // Duplicated as state and ref: state re-renders the tab totals and margin bar, the ref is what
    // the flush callback reads without re-subscribing.
    const [livePending, setLivePending] = useState({});
    const pendingRef = useRef({});

    const liveOrder = useMemo(
        () => ({ ...clonedOrder, ...Object.values(livePending).reduce(mergeChanges, {}) }),
        [clonedOrder, livePending]
    );

    const liveAllClonedOrders = useMemo(
        () => allClonedOrders.map((o) => (o.order_id === order.order_id ? liveOrder : o)),
        [allClonedOrders, liveOrder, order.order_id]
    );

    useEffect(() => {
        onLiveOrderChange?.(liveOrder);
    }, [liveOrder]);

    useEffect(() => {
        if (!flushRef) return;
        flushRef.current = () => {
            const merged = Object.values(pendingRef.current).reduce(mergeChanges, {});
            if (Object.keys(merged).length > 0) {
                return onFlushSave(order.order_id, merged);
            }
            return Promise.resolve();
        };
    }, [flushRef, onFlushSave, order.order_id]);

    // modifyOrder routes to onFlushSave so an explicit "Save Charges" click persists immediately
    // rather than waiting for the pending-change flush.
    const shims = useMemo(
        () =>
            Object.fromEntries(
                tabs.map(({ perspective }) => [
                    perspective,
                    {
                        state: { order: clonedOrder },
                        callbacks: {
                            modifyOrder: (changes) => onFlushSave(order.order_id, changes),
                            onPendingChange: (updates) => {
                                pendingRef.current = { ...pendingRef.current, [perspective]: updates };
                                setLivePending((prev) => ({ ...prev, [perspective]: updates }));
                            },
                        },
                    },
                ])
            ),
        [tabs, clonedOrder, onFlushSave, order.order_id]
    );

    const selectedTab = tabs.some((tab) => tab.perspective === activeTab) ? activeTab : tabs[0]?.perspective;
    const lane = [order.dropoff_city, order.dropoff_state].filter(Boolean).join(', ');

    const tabBtn = (perspective, label, accent, total) => {
        const active = selectedTab === perspective;
        return (
            <button
                key={perspective}
                onClick={() => setActiveTab(perspective)}
                css={css`
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-family: Montserrat, Roboto, Arial, sans-serif;
                    font-weight: 700;
                    font-size: 14px;
                    padding: 11px 16px;
                    cursor: pointer;
                    background: transparent;
                    color: ${active ? onwardNavy : greys[1]};
                    border: none;
                    border-bottom: 2px solid ${active ? accent : 'transparent'};
                    margin-bottom: -1px;
                `}
            >
                <span
                    css={css`
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background: ${accent};
                        flex-shrink: 0;
                    `}
                />
                {label}&nbsp;
                <span
                    css={css`
                        color: ${greys[1]};
                        font-weight: 800;
                    `}
                >
                    {fmt$.format(total)}
                </span>
            </button>
        );
    };

    return (
        <div
            css={css`
                height: 100%;
                width: 100%;
                display: flex;
                flex-direction: row;
                min-height: 0;
                overflow: hidden;
            `}
        >
            {/* Main audit column — scrolls independently of the detail rail */}
            <div
                css={css`
                    flex: 1;
                    min-width: 0;
                    overflow-y: auto;
                    padding: 22px 28px 30px;
                    display: flex;
                    flex-direction: column;
                    gap: 18px;
                `}
            >
                {/* Order header */}
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    `}
                >
                    <span
                        css={css`
                            font-family: Montserrat, Roboto, Arial, sans-serif;
                            font-weight: 700;
                            font-size: 12px;
                            color: ${greys[2]};
                            background: ${greys[5]};
                            padding: 4px 10px;
                            border-radius: 20px;
                            white-space: nowrap;
                            flex-shrink: 0;
                        `}
                    >
                        Order {orderIndex + 1} of {totalOrders}
                    </span>
                    <span
                        css={css`
                            font-family: Montserrat, Roboto, Arial, sans-serif;
                            font-weight: 800;
                            font-size: 20px;
                            color: ${onwardNavy};
                            letter-spacing: -0.01em;
                            white-space: nowrap;
                        `}
                    >
                        {order.order_number || order.po_number || order.reference_id}
                    </span>
                    {lane && (
                        <span
                            css={css`
                                font-family: Montserrat, Roboto, Arial, sans-serif;
                                font-weight: 600;
                                font-size: 13px;
                                color: ${greys[1]};
                                min-width: 0;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                white-space: nowrap;
                            `}
                        >
                            {lane}
                        </span>
                    )}
                    <a
                        href={`/order/${order.order_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        css={css`
                            margin-left: auto;
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            width: 32px;
                            height: 32px;
                            border-radius: 6px;
                            background: ${greens[0]};
                            text-decoration: none;
                            flex-shrink: 0;
                            &:hover {
                                background: #187522;
                            }
                        `}
                    >
                        <LaunchIcon style={{ fontSize: 15, color: 'white' }} />
                    </a>
                </div>

                {/* Margin bar — uses liveOrder so values update as the user edits */}
                <MarginBar
                    clonedOrder={liveOrder}
                    allClonedOrders={liveAllClonedOrders}
                    baselineRouteMargin={baselineRouteMargin}
                    showRouteMargin={showRouteFigures}
                    showShipperFigures={showShipperFigures}
                />

                {/* Tabbed rate area */}
                <div>
                    <div
                        css={css`
                            display: flex;
                            border-bottom: 1px solid ${greys[4]};
                            margin-bottom: 16px;
                        `}
                    >
                        {tabs.map(({ perspective, label, side }) =>
                            tabBtn(
                                perspective,
                                label,
                                side === 'shipper' ? blues[0] : golds[0],
                                orderPerspectiveTotal(liveOrder, perspective)
                            )
                        )}
                    </div>

                    {/* Hidden with display:none rather than unmounted, so each panel's local edit state survives a tab switch. */}
                    {tabs.map(({ perspective }) => (
                        <div
                            key={perspective}
                            css={css`
                                display: ${selectedTab === perspective ? 'block' : 'none'};
                            `}
                        >
                            <ModalContext.Provider value={shims[perspective]}>
                                <OrderDetailsPricing
                                    perspective={perspective}
                                    defaultExpanded
                                    canEdit={canEditPerspective ? !!canEditPerspective(order, perspective) : undefined}
                                />
                            </ModalContext.Provider>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right detail rail — overview header + tabbed order info */}
            <DetailPanel order={order} />
        </div>
    );
}
